import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const faqs = [
  {
    category: "Brewing",
    questions: [
      {
        q: "How do I brew Mudbutt Brew teas?",
        a: "Steep 1 tablespoon per 8 oz of hot water (195°F) for 15 minutes, covered. Strain and enjoy. Check individual product labels for any blend-specific instructions."
      },
      {
        q: "Can I reuse the tea leaves?",
        a: "Yes! Most of our blends can be steeped 2-3 times. The second steep often brings out different flavor notes. Just add a minute or two to steep time for subsequent brews."
      },
      {
        q: "Do I need any special equipment?",
        a: "A tea strainer or infuser works great. For blends containing Mullein (Elder & Fire, Lung Moon), we recommend using a fine mesh strainer or coffee filter to catch the fine plant hairs."
      }
    ]
  },
  {
    category: "Storage & Freshness",
    questions: [
      {
        q: "How should I store my tea?",
        a: "Store in a cool, dry place away from direct sunlight. Keep the bag sealed or transfer to an airtight container. Avoid storing near strong odors as tea can absorb them."
      },
      {
        q: "How long does the tea stay fresh?",
        a: "Our loose leaf teas stay fresh for 12-18 months when stored properly. You'll notice the aroma fading when it's time to replace your stash."
      }
    ]
  },
  {
    category: "Ingredients & Sourcing",
    questions: [
      {
        q: "Where do you source your herbs?",
        a: "We use a combination of wild-crafted and organically grown herbs from trusted suppliers. Many ingredients are sourced from small farms and ethical wildcrafters."
      },
      {
        q: "Are your teas organic?",
        a: "While not all ingredients are certified organic, we prioritize high-quality, naturally grown herbs. We never use artificial flavors, colors, or preservatives."
      },
      {
        q: "Do your teas contain caffeine?",
        a: "Most of our blends are caffeine-free. However, Forge Black, Leaf Forge, and Gray Beard Mountain Hunter contain green or black tea and do have caffeine. Check individual product labels."
      }
    ]
  },
  {
    category: "Safety & Usage",
    questions: [
      {
        q: "Are there any side effects?",
        a: "Herbal teas are generally safe for most people. However, some herbs may cause reactions in sensitive individuals. Start with a small amount to see how your body responds."
      },
      {
        q: "Can I drink these teas while pregnant or nursing?",
        a: "Please consult your healthcare provider before using any herbal products if you are pregnant, nursing, or trying to conceive. Some herbs are not recommended during pregnancy."
      },
      {
        q: "Will these teas interact with my medications?",
        a: "Some herbs can interact with medications. If you're taking prescription drugs, especially blood thinners, blood pressure medications, or sedatives, please consult your doctor before use."
      },
      {
        q: "Are these teas safe for children?",
        a: "We recommend consulting with a pediatrician before giving herbal teas to children. If approved, use a smaller amount (1/4 to 1/2 the adult serving)."
      }
    ]
  }
];

export const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <section id="faq" className="py-20" style={{ backgroundColor: '#f5e9d0' }}>
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ 
              color: '#5e3a1c',
              fontFamily: 'Georgia, serif'
            }}
          >
            Frequently Asked Questions
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#8b5c2f' }}>
            Everything you need to know about our herbal brews
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-8">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              <h3 
                className="text-xl font-bold mb-4 pb-2 border-b-2"
                style={{ color: '#5e3a1c', borderColor: '#c2a357', fontFamily: 'Georgia, serif' }}
              >
                {category.category}
              </h3>
              <div className="space-y-3">
                {category.questions.map((item, questionIndex) => {
                  const key = `${categoryIndex}-${questionIndex}`;
                  const isOpen = openItems[key];
                  return (
                    <div 
                      key={questionIndex}
                      className="rounded-lg overflow-hidden"
                      style={{ backgroundColor: 'white', border: '1px solid #c2a357' }}
                    >
                      <button
                        onClick={() => toggleItem(categoryIndex, questionIndex)}
                        className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-opacity-50 transition-colors"
                        style={{ backgroundColor: isOpen ? 'rgba(194, 163, 87, 0.1)' : 'white' }}
                      >
                        <span className="font-medium" style={{ color: '#5e3a1c' }}>{item.q}</span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: '#c2a357' }} />
                        ) : (
                          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: '#c2a357' }} />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-4">
                          <p style={{ color: '#8b5c2f' }}>{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
